import React, { Component } from 'react';
import { VehicleSpecificPricing } from './partial/VehicleSpecificPricing';
import BootstrapTable from 'react-bootstrap-table-next';
import { Modal, Button, Form, Row, Col, Table } from 'react-bootstrap';
import "react-bootstrap-table/css/react-bootstrap-table.css";

import { HTTPService } from "../services/httpService";

const API = new HTTPService();

export default class Vehicles extends Component {
    state = {
        // Vehicle catalog data
        vehicles: [], // Combined view of all vehicles (make/model/trim)
        groupedVehicles: [], // Grouped data for table display
        makes: [],
        models: [],
        trims: [],
        
        // Search/Filter
        searchTerm: '',
        selectedMakeFilter: 'All Makes',
        
        // Add Vehicle Modal - Year-based filtering
        showAddVehicle: false,
        newVehicle: {
            year: new Date().getFullYear(),
            make: '',
            makeId: '',
            model: '',
            modelId: '',
            trim: '',
            bodyStyle: 'Sedan',
            size: 'M'
        },
        
        // Available options based on year
        availableMakes: [],
        availableModels: [],
        availableTrims: [],
        
        // Loading states
        loading: false,
        saving: false,
        loadingMakes: false,
        loadingModels: false,
        loadingTrims: false,
        
        // Price Override Modal (keep if still needed)
        showPriceOverride: false,
        selectedVehicleForOverride: null,
        
        // NEW: Service Override Modal
        showServiceOverrides: false,
        selectedVehicleForOverrides: null,
        overrideCounts: {}, // Cache for override counts
        
        // NEW: Trims Detail Modal
        showTrimsModal: false,
        selectedGroupForTrims: null,
        
        // Validation
        errors: {}
    }

    async componentDidMount() {
        await this.loadVehicleCatalog();
        await this.loadAllMakes(); // For filter dropdown
    }

    // =====================
    // Data Loading
    // =====================

    loadVehicleCatalog = async () => {
        this.setState({ loading: true });
        try {
            // Get all vehicles with joined data and override counts
            const { data: vehicles } = await API.get(`/vehiclescatalog/${this.props.typeId}`);
             // DEBUG: See what's actually coming from the API
        //console.log('Raw vehicle data:', vehicles);
        //console.log('Vehicles with override_count > 0:', vehicles.filter(v => v.override_count > 0);
            
            // Extract override counts from the vehicle data
            const overrideCounts = {};
            vehicles.forEach(vehicle => {
                overrideCounts[vehicle.id_car_trim] = vehicle.override_count || 0;
            });
            
            // Group the vehicles
            const groupedVehicles = this.groupVehicles(vehicles);
            
            this.setState({ 
                vehicles, 
                groupedVehicles,
                overrideCounts,
                loading: false 
            });
        } catch (error) {
            console.error("Error loading vehicle catalog:", error);
            this.setState({ loading: false });
        }
    }

    // NEW: Group vehicles by make and model only (ignore year ranges to avoid duplicates)
    groupVehicles = (vehicles) => {
        const groups = {};
        
        vehicles.forEach(vehicle => {
            // Create a key for grouping by make and model only
            const groupKey = `${vehicle.make_name}|${vehicle.model_name}`;
            
            if (!groups[groupKey]) {
                groups[groupKey] = {
                    id: groupKey,
                    make_name: vehicle.make_name,
                    model_name: vehicle.model_name,
                    trims: [],
                    trim_count: 0,
                    total_service_overrides: 0,
                    start_years: [],
                    end_years: []
                };
            }
            
            // Add trim to the group
            groups[groupKey].trims.push({
                id_car_trim: vehicle.id_car_trim,
                trim_name: vehicle.trim_name,
                body_style: vehicle.body_style,
                id_car_type: vehicle.id_car_type,
                size_label: this.getSizeLabel(vehicle.id_car_type),
                override_count: vehicle.override_count || 0,
                start_production_year: vehicle.start_production_year,
                end_production_year: vehicle.end_production_year,
                // Keep the full vehicle data for service overrides
                fullVehicleData: vehicle
            });
            
            // Track years for the group
            groups[groupKey].start_years.push(vehicle.start_production_year);
            if (vehicle.end_production_year) {
                groups[groupKey].end_years.push(vehicle.end_production_year);
            }
            
            groups[groupKey].trim_count = groups[groupKey].trims.length;
            groups[groupKey].total_service_overrides += (vehicle.override_count || 0);
        });
        
        // Calculate year ranges for each group
        Object.values(groups).forEach(group => {
            const minStart = Math.min(...group.start_years);
            const maxEnd = group.end_years.length > 0 ? Math.max(...group.end_years) : null;
            group.year_range = `${minStart}-${maxEnd || 'Present'}`;
            group.start_production_year = minStart;
            group.end_production_year = maxEnd;
        });
        
        return Object.values(groups);
    }

    loadAllMakes = async () => {
        try {
            const { data: makes } = await API.get('/makes');
            this.setState({ makes });
        } catch (error) {
            console.error("Error loading makes:", error);
        }
    }

    // =====================
    // Year-based filtering for Add Vehicle
    // =====================

    loadMakesForYear = async (year) => {
        this.setState({ loadingMakes: true });
        try {
            // Get makes available for the selected year
            const { data: makes } = await API.get(`/makes/${year}`);
            this.setState({ 
                availableMakes: makes,
                availableModels: [],
                availableTrims: [],
                loadingMakes: false 
            });
            
            // Reset dependent selections
            this.handleNewVehicleChange('make', '');
            this.handleNewVehicleChange('model', '');
            this.handleNewVehicleChange('trim', '');
        } catch (error) {
            console.error("Error loading makes for year:", error);
            this.setState({ loadingMakes: false });
        }
    }

// Enhanced loadModelsForMakeYear with debugging
loadModelsForMakeYear = async (makeId, year) => {
    this.setState({ loadingModels: true });
    try {
        //console.log('=== LOADING MODELS DEBUG ===');
        //console.log('makeId:', makeId);
        //console.log('year:', year);
        
        const url = `/models/${year}${makeId.toString().padStart(4, '0')}`;
        //console.log('API URL:', url);
        
        // Get models available for this make in the selected year
        const response = await API.get(url);
        const models = response.data;
        
        //console.log('✅ Received models from server:');
        //console.log('Number of models:', models.length);
        
        /* Log each model received
        models.forEach((model, index) => {
            console.log(`Model ${index + 1}:`
                 , 
            {
                id_car_model: model.id_car_model,
                name: model.name,
                id_car_type: model.id_car_type,
                id_car_type_type: typeof model.id_car_type
            });
            
            // Special attention to 8C Competizione
            if (model.name.includes('8C') || model.name.includes('Competizione')) {
                //console.log('🔍 8C Competizione details:', model);
            }
        }); */
        
        this.setState({ 
            availableModels: models,
            availableTrims: [],
            loadingModels: false 
        });
        
        // Reset dependent selections
        this.handleNewVehicleChange('model', '');
        this.handleNewVehicleChange('trim', '');
        
    } catch (error) {
        console.error("❌ Error loading models:", error);
        console.error("Error response data:", error.response?.data);
        this.setState({ loadingModels: false });
    }
}
    loadTrimsForModel = async (modelId) => {
        this.setState({ loadingTrims: true });
        try {
            // Get existing trims for this model
            const { data: trims } = await API.get(`/trims/${modelId}`);
            this.setState({ 
                availableTrims: trims,
                loadingTrims: false 
            });
        } catch (error) {
            console.error("Error loading trims:", error);
            this.setState({ loadingTrims: false });
        }
    }

    // =====================
    // Add Vehicle Logic
    // =====================

// Enhanced debug version of addVehicle method in React component
addVehicle = async () => {
    if (!this.validateVehicle()) return;

    this.setState({ saving: true });
    try {
        const { year, make, model, trim, bodyStyle, size } = this.state.newVehicle;
        
        // Single API call that handles all the complexity
        await API.post('/vehiclesadd', {
            year: parseInt(year),
            make: make.trim(),
            model: model.trim(), 
            trim: trim.trim(),
            bodyStyle,
            size,
            usa: 1
        });

        // Reset form and refresh data
        this.setState({
            showAddVehicle: false,
            newVehicle: {
                year: new Date().getFullYear(),
                make: '',
                makeId: '',
                model: '',
                modelId: '',
                trim: '',
                bodyStyle: 'Sedan', 
                size: 'M'
            },
            errors: {},
            saving: false,
            availableMakes: [],
            availableModels: [],
            availableTrims: []
        });

        await this.loadVehicleCatalog();
        await this.loadAllMakes();
        
    } catch (error) {
        console.error("Error adding vehicle:", error);
        this.setState({ 
            saving: false,
            errors: { general: "Failed to add vehicle. Please try again." }
        });
    }
}
// Also enhance the validateVehicle method for debugging
validateVehicle = () => {
    const { year, make, model, trim } = this.state.newVehicle;
    const errors = {};

    //console.log('=== CLIENT VALIDATION ===');
    //console.log('Validating:', { year, make, model, trim });

    if (!year || year < 1900 || year > new Date().getFullYear() + 2) {
        errors.year = "Please enter a valid year";
        //console.log('❌ Year validation failed:', year);
    }
    if (!make.trim()) {
        errors.make = "Make is required";
        //console.log('❌ Make validation failed:', make);
    }
    if (!model.trim()) {
        errors.model = "Model is required";
        //console.log('❌ Model validation failed:', model);
    }
    if (!trim.trim()) {
        errors.trim = "Trim is required";
        //console.log('❌ Trim validation failed:', trim);
    }

    //console.log('Client validation errors:', errors);
    //console.log('Validation result:', Object.keys(errors).length === 0 ? '✅ PASSED' : '❌ FAILED');

    this.setState({ errors });
    return Object.keys(errors).length === 0;
}
    // =====================
    // Event Handlers
    // =====================

// Clean handleTypeaheadChange method - just the fix
handleTypeaheadChange = async (field, value) => {
    // Handle make/model typeahead inputs
    this.setState({
        newVehicle: {
            ...this.state.newVehicle,
            [field]: value
        },
        errors: {
            ...this.state.errors,
            [field]: ''
        }
    });

    if (field === 'make') {
        // Check if this is an existing make
        const existingMake = this.state.availableMakes.find(make => 
            make.name.toLowerCase() === value.toLowerCase()
        );
        
        if (existingMake) {
            // Existing make - load its models
            this.setState({
                newVehicle: {
                    ...this.state.newVehicle,
                    make: value,
                    makeId: existingMake.id_car_make,
                    model: '', // Reset dependent fields
                    trim: ''
                }
            });
            await this.loadModelsForMakeYear(existingMake.id_car_make, this.state.newVehicle.year);
        } else {
            // New make - clear dependent data
            this.setState({
                newVehicle: {
                    ...this.state.newVehicle,
                    make: value,
                    makeId: '',
                    model: '',
                    trim: ''
                },
                availableModels: [],
                availableTrims: []
            });
        }
    } else if (field === 'model') {
        // Check if this is an existing model
        const existingModel = this.state.availableModels.find(model => 
            model.name.toLowerCase() === value.toLowerCase()
        );
        
        if (existingModel) {
            // Existing model - load its trims and set size
            this.setState({
                newVehicle: {
                    ...this.state.newVehicle,
                    model: value,
                    modelId: existingModel.id_car_model,
                    size: existingModel.vehicleSize || existingModel.id_car_type || 'M',
                    trim: ''
                }
            });
            await this.loadTrimsForModel(existingModel.id_car_model);
        } else {
            // New model - clear trims but keep size editable
            this.setState({
                newVehicle: {
                    ...this.state.newVehicle,
                    model: value,
                    modelId: '',
                    trim: '',
                    size: this.state.newVehicle.size || 'M'
                },
                availableTrims: []
            });
        }
    }
}

    handleNewVehicleChange = async (field, value) => {
        this.setState({
            newVehicle: {
                ...this.state.newVehicle,
                [field]: value
            },
            errors: {
                ...this.state.errors,
                [field]: '' // Clear error when user starts typing
            }
        });

        // Handle year change
        if (field === 'year') {
            await this.loadMakesForYear(value);
        }
    }

    handleSearch = (event) => {
        this.setState({ searchTerm: event.target.value }, () => {
            // Re-group vehicles after search change
            const filteredVehicles = this.getFilteredVehicles();
            const groupedVehicles = this.groupVehicles(filteredVehicles);
            this.setState({ groupedVehicles });
        });
    }

    handleMakeFilter = (event) => {
        this.setState({ selectedMakeFilter: event.target.value }, () => {
            // Re-group vehicles after filter change
            const filteredVehicles = this.getFilteredVehicles();
            const groupedVehicles = this.groupVehicles(filteredVehicles);
            this.setState({ groupedVehicles });
        });
    }

    // =====================
    // Modal Methods
    // =====================

    openAddVehicleModal = async () => {
        this.setState({ showAddVehicle: true });
        // Load makes for current year when modal opens
        await this.loadMakesForYear(this.state.newVehicle.year);
    }

    closeAddVehicleModal = () => {
        this.setState({ 
            showAddVehicle: false,
            availableMakes: [],
            availableModels: [],
            availableTrims: [],
            newVehicle: {
                year: new Date().getFullYear(),
                make: '',
                makeId: '',
                model: '',
                modelId: '',
                trim: '',
                bodyStyle: 'Sedan',
                size: 'M'
            },
            errors: {}
        });
    }

    // =====================
    // Price Override Methods (keep if still needed)
    // =====================

    openPriceOverrideModal = (vehicle) => {
        this.setState({
            showPriceOverride: true,
            selectedVehicleForOverride: vehicle
        });
    }

    closePriceOverrideModal = () => {
        this.setState({
            showPriceOverride: false,
            selectedVehicleForOverride: null
        });
    }

    handlePriceOverrideUpdated = async () => {
        // Reload the vehicle catalog to get updated prices
        await this.loadVehicleCatalog();
        this.closePriceOverrideModal();
    }

    // =====================
    // NEW: Service Override Methods
    // =====================

    openServiceOverridesModal = (vehicle) => {
        // Format vehicle data for VehicleSpecificPricing component
        const formattedVehicle = {
            make: vehicle.make_name,
            model: vehicle.model_name,
            trimId: vehicle.id_car_trim,
            trimName: vehicle.trim_name,
            vehicleSize: vehicle.id_car_type // S, M, or L
        };

        this.setState({
            showServiceOverrides: true,
            selectedVehicleForOverrides: formattedVehicle
        });
    }

    closeServiceOverridesModal = () => {
        this.setState({
            showServiceOverrides: false,
            selectedVehicleForOverrides: null
        });
    }

    handleServiceOverridesUpdated = async () => {
        // Reload the vehicle catalog (which includes override counts)
        // but DON'T close the modal - let user close it manually
        await this.loadVehicleCatalog();
    }

    // =====================
    // NEW: Trims Modal Methods
    // =====================

    openTrimsModal = (group) => {
        this.setState({
            showTrimsModal: true,
            selectedGroupForTrims: group
        });
    }

    closeTrimsModal = () => {
        this.setState({
            showTrimsModal: false,
            selectedGroupForTrims: null
        });
    }

    // =====================
    // Helper Methods
    // =====================

    getFilteredVehicles = () => {
        let filtered = this.state.vehicles;

        // Filter by search term
        if (this.state.searchTerm) {
            const term = this.state.searchTerm.toLowerCase();
            filtered = filtered.filter(vehicle =>
                vehicle.make_name.toLowerCase().includes(term) ||
                vehicle.model_name.toLowerCase().includes(term) ||
                vehicle.trim_name.toLowerCase().includes(term)
            );
        }

        // Filter by make
        if (this.state.selectedMakeFilter !== 'All Makes') {
            filtered = filtered.filter(vehicle => 
                vehicle.make_name === this.state.selectedMakeFilter
            );
        }

        return filtered;
    }

    getSizeLabel = (size) => {
        const sizeMap = { S: 'Small', M: 'Medium', L: 'Large' };
        return sizeMap[size] || size;
    }

    // =====================
    // Render Methods
    // =====================

    renderTrimsModal = () => {
        const { showTrimsModal, selectedGroupForTrims } = this.state;
        
        if (!selectedGroupForTrims) return null;

        return (
            <Modal 
                show={showTrimsModal} 
                onHide={this.closeTrimsModal}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        Trims for {selectedGroupForTrims.year_range} {selectedGroupForTrims.make_name} {selectedGroupForTrims.model_name}
                    </Modal.Title>
                </Modal.Header>
                
                <Modal.Body>
                    <Table striped hover responsive>
                        <thead>
                            <tr>
                                <th>Trim</th>
                                <th>Body Style</th>
                                <th>Size</th>
                            </tr>
                        </thead>
                        <tbody>
                            {selectedGroupForTrims.trims.map((trim, index) => (
                                <tr key={trim.id_car_trim}>
                                    <td><strong>{trim.trim_name}</strong></td>
                                    <td>{trim.body_style}</td>
                                    <td>{trim.size_label}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Modal.Body>
                
                <Modal.Footer>
                    <Button variant="secondary" onClick={this.closeTrimsModal}>
                        CLOSE
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    renderAddVehicleModal = () => {
        const { showAddVehicle, newVehicle, saving, errors, loadingMakes, loadingModels, loadingTrims } = this.state;
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let year = currentYear + 2; year >= 1950; year--) {
            years.push(year);
        }

        // Check if selected trim already exists
        const trimExists = this.state.availableTrims.some(trim => 
            trim.name.toLowerCase() === newVehicle.trim.toLowerCase()
        );

        // Check if make/model are new
        const isNewMake = newVehicle.make && !this.state.availableMakes.some(make => 
            make.name.toLowerCase() === newVehicle.make.toLowerCase()
        );
        const isNewModel = newVehicle.model && !this.state.availableModels.some(model => 
            model.name.toLowerCase() === newVehicle.model.toLowerCase()
        );

        return (
            <Modal 
                show={showAddVehicle} 
                onHide={this.closeAddVehicleModal}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Add New Vehicle</Modal.Title>
                </Modal.Header>
                
                <Modal.Body>
                    {errors.general && (
                        <div className="alert alert-danger">{errors.general}</div>
                    )}
                    
                    <Form>
                        <Row>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Year</Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={newVehicle.year}
                                        onChange={(e) => this.handleNewVehicleChange('year', e.target.value)}
                                        isInvalid={!!errors.year}
                                    >
                                        {years.map(year => (
                                            <option key={year} value={year}>{year}</option>
                                        ))}
                                    </Form.Control>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.year}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>
                                        Make 
                                        {isNewMake && <small className="text-success ml-2">(New Make)</small>}
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Type to search or add new make..."
                                        value={newVehicle.make}
                                        onChange={(e) => this.handleTypeaheadChange('make', e.target.value)}
                                        onFocus={() => this.setState({ showMakeSuggestions: true })}
                                        onBlur={() => setTimeout(() => this.setState({ showMakeSuggestions: false }), 200)}
                                        isInvalid={!!errors.make}
                                        disabled={loadingMakes}
                                        list="make-suggestions"
                                    />
                                    <datalist id="make-suggestions">
                                        {this.state.availableMakes.map(make => (
                                            <option key={make.id_car_make} value={make.name} />
                                        ))}
                                    </datalist>
                                    {loadingMakes && <small className="text-muted">Loading makes...</small>}
                                    <Form.Control.Feedback type="invalid">
                                        {errors.make}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        
                        <Row>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>
                                        Model
                                        {isNewModel && <small className="text-success ml-2">(New Model)</small>}
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Type to search or add new model..."
                                        value={newVehicle.model}
                                        onChange={(e) => this.handleTypeaheadChange('model', e.target.value)}
                                        onFocus={() => this.setState({ showModelSuggestions: true })}
                                        onBlur={() => setTimeout(() => this.setState({ showModelSuggestions: false }), 200)}
                                        isInvalid={!!errors.model}
                                        disabled={!newVehicle.make || loadingModels}
                                        list="model-suggestions"
                                    />
                                    <datalist id="model-suggestions">
                                        {this.state.availableModels.map(model => (
                                            <option key={model.id_car_model} value={model.name} />
                                        ))}
                                    </datalist>
                                    {loadingModels && <small className="text-muted">Loading models...</small>}
                                    <Form.Control.Feedback type="invalid">
                                        {errors.model}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Trim</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter trim name"
                                        value={newVehicle.trim}
                                        onChange={(e) => this.handleNewVehicleChange('trim', e.target.value)}
                                        isInvalid={!!errors.trim}
                                        disabled={!newVehicle.model}
                                        list="trim-suggestions"
                                    />
                                    <datalist id="trim-suggestions">
                                        {this.state.availableTrims.map(trim => (
                                            <option key={trim.id_car_trim} value={trim.name} />
                                        ))}
                                    </datalist>
                                    {trimExists && (
                                        <small className="text-warning">
                                            <i className="fa fa-exclamation-triangle"></i> This trim already exists
                                        </small>
                                    )}
                                    {loadingTrims && <small className="text-muted">Loading existing trims...</small>}
                                    <Form.Control.Feedback type="invalid">
                                        {errors.trim}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        
                        <Row>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Body Style</Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={newVehicle.bodyStyle}
                                        onChange={(e) => this.handleNewVehicleChange('bodyStyle', e.target.value)}
                                    >
                                        <option value="Sedan">Sedan</option>
                                        <option value="Coupe">Coupe</option>
                                        <option value="SUV">SUV</option>
                                        <option value="Wagon">Wagon</option>
                                        <option value="Convertible">Convertible</option>
                                        <option value="Hatchback">Hatchback</option>
                                        <option value="Truck">Truck</option>
                                        <option value="Crossover">Crossover</option>
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Vehicle Size</Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={newVehicle.size}
                                        onChange={(e) => this.handleNewVehicleChange('size', e.target.value)}
                                        disabled={!!newVehicle.model && !isNewModel} // Disabled if existing model sets the size
                                    >
                                        <option value="S">Small</option>
                                        <option value="M">Medium</option>
                                        <option value="L">Large</option>
                                    </Form.Control>
                                    {newVehicle.model && !isNewModel && (
                                        <small className="text-muted">Size automatically set by existing model</small>
                                    )}
                                    {isNewModel && (
                                        <small className="text-info">Choose size for new model</small>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>

                    </Form>
                </Modal.Body>
                
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={this.closeAddVehicleModal}
                        disabled={saving}
                    >
                        CANCEL
                    </Button>
                    <Button
                        variant="primary"
                        onClick={this.addVehicle}
                        disabled={saving || (trimExists && !isNewModel)}
                    >
                        {saving ? (
                            <>
                                <i className="fa fa-spinner fa-spin mr-2"></i>
                                ADDING...
                            </>
                        ) : (
                            trimExists && !isNewModel ? 'TRIM ALREADY EXISTS' : 'ADD VEHICLE'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    render() {
        const {
            loading,
            groupedVehicles,
            showServiceOverrides,
            selectedVehicleForOverrides
        } = this.state;
        
        // Prepare make filter options from original vehicles data
        const uniqueMakes = [...new Set(this.state.vehicles.map(v => v.make_name))].sort();
        const makeOptions = ['All Makes', ...uniqueMakes];

        // Updated table columns for grouped view
        const columns = [{
            dataField: 'year_range',
            text: 'Years',
            sort: true,
            headerStyle: { width: '120px' }
        }, {
            dataField: 'make_name',
            text: 'Make',
            sort: true,
            headerStyle: { width: '120px' }
        }, {
            dataField: 'model_name', 
            text: 'Model',
            sort: true,
            headerStyle: { width: '150px' }
        }, {
            dataField: 'trim_count',
            text: 'Trims',
            formatter: (cell, row) => (
                <button
                    className="btn btn-link text-primary p-0"
                    onClick={() => this.openTrimsModal(row)}
                    title={`Click to view ${cell} trim${cell !== 1 ? 's' : ''}`}
                    style={{ textDecoration: 'underline', fontSize: '14px' }}
                >
                    {cell} trim{cell !== 1 ? 's' : ''}
                </button>
            ),
            sort: true,
            headerStyle: { width: '120px' },
            style: { textAlign: 'center' }
        }, {
            dataField: 'service_overrides',
            text: 'Custom Pricing',
            formatter: (cell, row) => {
                const totalOverrides = row.total_service_overrides;

                if (totalOverrides === 0) {
                    return (
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => this.openServiceOverridesModal(row.trims[0].fullVehicleData)}
                            title="No custom pricing - click to add"
                        >
                            <i className="fa fa-plus mr-1"></i>
                            ADD PRICING
                        </button>
                    );
                }

                return (
                    <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => this.openServiceOverridesModal(row.trims[0].fullVehicleData)}
                        title={`${totalOverrides} custom price${totalOverrides !== 1 ? 's' : ''} - click to manage`}
                    >
                        <i className="fa fa-dollar-sign mr-1"></i>
                        {totalOverrides} CUSTOM PRICE{totalOverrides !== 1 ? 'S' : ''}
                    </button>
                );
            },
            sort: true,
            headerStyle: { width: '180px' },
            style: { textAlign: 'center' }
        }];

        return (
            <React.Fragment>
                {/* Search and Filter Controls */}
                <div className="card mb-4">
                    <div className="card-body">
                        <Row>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Search Vehicles</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Search make, model, or trim..."
                                        value={this.state.searchTerm}
                                        onChange={this.handleSearch}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>Filter by Make</Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={this.state.selectedMakeFilter}
                                        onChange={this.handleMakeFilter}
                                    >
                                        {makeOptions.map(make => (
                                            <option key={make} value={make}>{make}</option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                            <Col md={5} className="d-flex align-items-end justify-content-end">
                                <Button
                                    variant="primary"
                                    onClick={this.openAddVehicleModal}
                                    disabled={loading}
                                >
                                    ADD NEW VEHICLE
                                </Button>
                            </Col>
                        </Row>
                    </div>
                </div>

                {/* Vehicle Table */}
                <div className="card">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-4">
                                <i className="fa fa-spinner fa-spin fa-2x"></i>
                                <p className="mt-2">Loading vehicle catalog...</p>
                            </div>
                        ) : (
                            <>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="mb-0">
                                        {groupedVehicles.length} vehicle group{groupedVehicles.length !== 1 ? 's' : ''} found
                                        <small className="text-muted ml-2">
                                            ({this.getFilteredVehicles().length} individual vehicles)
                                        </small>
                                    </h6>
                                </div>
                                
                                <BootstrapTable
                                    keyField='id'
                                    data={groupedVehicles}
                                    columns={columns}
                                    striped
                                    hover
                                    condensed
                                    defaultSorted={[{
                                        dataField: 'make_name',
                                        order: 'asc'
                                    }]}
                                    noDataIndication="No vehicle groups found"
                                />
                            </>
                        )}
                    </div>
                </div>

                {/* Add Vehicle Modal */}
                {this.renderAddVehicleModal()}
                
                {/* NEW: Trims Detail Modal */}
                {this.renderTrimsModal()}
                
                {/* Vehicle-Specific Pricing Modal */}
                {showServiceOverrides && selectedVehicleForOverrides && (
                    <VehicleSpecificPricing
                        show={showServiceOverrides}
                        onHide={this.closeServiceOverridesModal}
                        installerId={this.props.typeId}
                        preSelectedVehicle={selectedVehicleForOverrides}
                        onUpdate={this.handleServiceOverridesUpdated}
                    />
                )}
            </React.Fragment>
        );
    }
}